import React, {
  Dispatch,
  RefObject,
  MutableRefObject,
  useLayoutEffect,
  useState,
  useCallback
} from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { classes } from './styles';
import {
  handleScrollLeft,
  handleScrollRight,
  handleScroll,
  handleScrollableEnter,
  handleScrollableExit
} from './tableHelper';
import { Box } from '@mui/material';
import { GenericTableData, TSortableColumn, TTableState, TableScrollAction } from './types';

const ARROW_WIDTH = 40;
const ARROW_HEIGHT = 65;

interface ITableArrowProps<T> {
  tableRef: RefObject<HTMLDivElement>;
  titleCellRef: RefObject<HTMLTableCellElement>;
  actionCellRef: RefObject<HTMLTableCellElement>;
  parentRef: RefObject<HTMLDivElement>;
  tableState: TTableState<T, TSortableColumn[]>;
  dispatch: Dispatch<TableScrollAction>;
  hoverTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  isHoveringRef: MutableRefObject<boolean>;
}

const TableArrows = <TableArrowProps extends GenericTableData>({
  tableRef,
  titleCellRef,
  actionCellRef,
  parentRef,
  tableState,
  dispatch,
  hoverTimeoutRef,
  isHoveringRef
}: ITableArrowProps<TableArrowProps>) => {
  const [arrowPositions, setArrowPositions] = useState({
    left: 0,
    right: 0,
    top: 0
  });

  useLayoutEffect(() => {
    const updatePositions = () => {
      if (!titleCellRef?.current || !actionCellRef?.current || !parentRef?.current) {
        return;
      }

      const titleRect = titleCellRef.current.getBoundingClientRect();
      const actionRect = actionCellRef.current.getBoundingClientRect();
      const parentRect = parentRef.current.getBoundingClientRect();

      const headerRow = titleCellRef.current.closest('tr');
      const headerRowRect = headerRow?.getBoundingClientRect();
      const headerBottomOffset = 20;
      const minTop = headerRowRect ? headerRowRect.bottom + headerBottomOffset : parentRect.top;

      let scrollY = window.scrollY || window.pageYOffset || 0;
      let scrollableParent: HTMLElement | null = null;
      let current = parentRef.current.parentElement;

      while (current && !scrollableParent) {
        const overflowY = window.getComputedStyle(current).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
          scrollableParent = current;
          scrollY = current.scrollTop;
        }
        current = current.parentElement;
      }

      const baseTableQuarter = parentRect.top + parentRect.height / 4;
      const calculatedTop = baseTableQuarter - ARROW_HEIGHT / 2 + scrollY;
      const arrowTop = Math.max(calculatedTop, minTop);

      const positions = {
        left: titleRect.right,
        right: actionRect.left - ARROW_WIDTH,
        top: arrowTop
      };
      setArrowPositions(positions);
    };

    requestAnimationFrame(() => {
      updatePositions();
    });

    const timeoutId = setTimeout(() => {
      updatePositions();
    }, 100);

    const handleScrollEvent = () => {
      handleScroll(tableRef, dispatch);
      updatePositions();
    };
    const handleResize = () => {
      requestAnimationFrame(updatePositions);
    };

    const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
      let current = element?.parentElement;
      while (current) {
        const overflowY = window.getComputedStyle(current).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const tableElement = tableRef.current;
    const scrollableParent = parentRef.current ? findScrollableParent(parentRef.current) : null;

    if (tableElement) {
      tableElement.addEventListener('scroll', handleScrollEvent, { passive: true });
    }
    if (scrollableParent) {
      scrollableParent.addEventListener('scroll', handleScrollEvent, { passive: true });
    }
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScrollEvent, { capture: true, passive: true });

    return () => {
      clearTimeout(timeoutId);
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleScrollEvent);
      }
      if (scrollableParent) {
        scrollableParent.removeEventListener('scroll', handleScrollEvent);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScrollEvent, { capture: true });
    };
  }, [tableRef, titleCellRef, actionCellRef, parentRef, tableState.tableHovered, dispatch]);

  const leftArrowVisible = tableState.tableHovered && tableState.canScroll[0];
  const rightArrowVisible = tableState.tableHovered && tableState.canScroll[1];

  const handleArrowEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    isHoveringRef.current = true;
    handleScrollableEnter(tableRef, dispatch);
  }, [tableRef, dispatch, hoverTimeoutRef, isHoveringRef]);

  const handleArrowLeave = useCallback(() => {
    isHoveringRef.current = false;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        handleScrollableExit(dispatch);
      }
      hoverTimeoutRef.current = null;
    }, 400);
  }, [dispatch, hoverTimeoutRef, isHoveringRef]);

  return (
    <>
      <Box
        onMouseEnter={handleArrowEnter}
        onMouseLeave={handleArrowLeave}
        onClick={(e) => {
          e.stopPropagation();
          if (!tableRef.current) return;
          handleScrollableEnter(tableRef, dispatch);
          handleScrollLeft(tableRef);
          handleScroll(tableRef, dispatch);
          setTimeout(() => {
            handleScroll(tableRef, dispatch);
          }, 100);
        }}
        sx={{
          ...classes.leftArrow,
          ...(leftArrowVisible ? classes.showArrows : {}),
          display: leftArrowVisible ? 'flex !important' : 'none !important',
          position: 'fixed !important',
          left: `${arrowPositions.left}px !important`,
          top: `${arrowPositions.top}px !important`,
          zIndex: 9999,
          bottom: 'auto !important'
        }}
        id="left-arrow"
      >
        <KeyboardArrowLeftIcon fontSize="large" sx={{ color: '#CCCCCC' }} />
      </Box>
      <Box
        onMouseEnter={handleArrowEnter}
        onMouseLeave={handleArrowLeave}
        onClick={(e) => {
          e.stopPropagation();
          if (!tableRef.current) return;
          handleScrollableEnter(tableRef, dispatch);
          handleScrollRight(tableRef);
          handleScroll(tableRef, dispatch);
          setTimeout(() => {
            handleScroll(tableRef, dispatch);
          }, 100);
        }}
        sx={{
          ...classes.rightArrow,
          ...(rightArrowVisible ? classes.showArrows : {}),
          display: rightArrowVisible ? 'flex !important' : 'none !important',
          position: 'fixed !important',
          left: `${arrowPositions.right}px !important`,
          top: `${arrowPositions.top}px !important`,
          zIndex: 9999,
          bottom: 'auto !important'
        }}
        id="right-arrow"
      >
        <KeyboardArrowRightIcon fontSize="large" sx={{ color: '#CCCCCC' }} />
      </Box>
    </>
  );
};

export default TableArrows;
